import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

const empty = {
  name: '',
  provider: 'calendly',
  bookingUrl: '',
  leadSources: [],
  isActive: true
};

const extractLeadSources = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.leadSources)) {
    return response.leadSources;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.items)) {
    return response.items;
  }

  return [];
};

export default function BookingAppManagement() {
  const [apps, setApps] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /*
   * ============================================================
   * LOAD BOOKING APPS + LEAD SOURCE MASTER DATA
   * ============================================================
   */
  const load = async () => {
    setLoading(true);
    setError('');

    try {
      const [appsResponse, leadSourceResponse] = await Promise.all([
        api.getAdminBookingApps(),
        api.getMasterData('leadSources')
      ]);

      setApps(Array.isArray(appsResponse) ? appsResponse : []);

      const sources = extractLeadSources(leadSourceResponse)
        .filter((item) => item && (item.name || item.value))
        .map((item) => ({
          ...item,
          name: item.name || item.value
        }));

      setLeadSources(sources);
    } catch (e) {
      console.error('Failed to load booking apps/master data:', e);

      setError(
        e.response?.data?.error ||
        'Failed to load booking apps and Lead Source master data'
      );

      setApps([]);
      setLeadSources([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /*
   * ============================================================
   * FORM SUBMIT
   * ============================================================
   */
  const submit = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError('');

    try {
      if (editing) {
        await api.updateBookingApp(editing.id, form);
      } else {
        await api.createBookingApp(form);
      }

      setShow(false);
      setEditing(null);

      setForm({
        ...empty,
        leadSources: []
      });

      await load();
    } catch (e) {
      console.error('Failed to save booking app:', e);

      setError(
        e.response?.data?.error ||
        'Failed to save booking app'
      );
    } finally {
      setSaving(false);
    }
  };

  /*
   * ============================================================
   * EDIT
   * ============================================================
   */
  const edit = (app) => {
    const selectedSources = Array.isArray(app.leadSources)
      ? app.leadSources
      : [];

    setEditing(app);

    setForm({
      name: app.name || '',
      provider: app.provider || 'calendly',
      bookingUrl: app.bookingUrl || '',
      leadSources: selectedSources,
      isActive: Boolean(app.isActive)
    });

    setShow(true);
  };

  /*
   * ============================================================
   * DELETE
   * ============================================================
   */
  const remove = async (id) => {
    if (!window.confirm('Delete this booking app?')) {
      return;
    }

    try {
      await api.deleteBookingApp(id);
      await load();
    } catch (e) {
      console.error('Failed to delete booking app:', e);

      setError(
        e.response?.data?.error ||
        'Failed to delete booking app'
      );
    }
  };

  /*
   * ============================================================
   * OPEN ADD
   * ============================================================
   */
  const openAdd = () => {
    setEditing(null);

    setForm({
      ...empty,
      leadSources: []
    });

    setError('');
    setShow(true);
  };

  /*
   * ============================================================
   * TOGGLE LEAD SOURCE
   * ============================================================
   */
  const toggleLeadSource = (sourceName) => {
    setForm((current) => {
      const currentSources = Array.isArray(current.leadSources)
        ? current.leadSources
        : [];

      const exists = currentSources.includes(sourceName);

      return {
        ...current,
        leadSources: exists
          ? currentSources.filter(
              (source) => source !== sourceName
            )
          : [...currentSources, sourceName]
      };
    });
  };

  if (loading) {
    return (
      <LoadingSpinner message="Loading booking apps..." />
    );
  }

  return (
    <div>
      <div className="management-header">
        <div>
          <h2>Booking Apps</h2>

          <p className="text-muted">
            Configure one or multiple demo booking apps and map them to Lead Source.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={openAdd}
        >
          <i className="fas fa-plus" /> Add Booking App
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {show && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>
              {editing
                ? 'Edit Booking App'
                : 'Add Booking App'}
            </h2>

            <form onSubmit={submit}>
              {/* NAME */}
              <div className="form-group">
                <label>Name *</label>

                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      name: e.target.value
                    })
                  }
                  required
                />
              </div>

              {/* PROVIDER */}
              <div className="form-group">
                <label>Provider *</label>

                <select
                  className="form-control"
                  value={form.provider}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      provider: e.target.value
                    })
                  }
                >
                  <option value="calendly">
                    Calendly
                  </option>

                  <option value="microsoft">
                    Microsoft Bookings
                  </option>
                </select>
              </div>

              {/* BOOKING URL */}
              <div className="form-group">
                <label>Booking URL</label>

                <input
                  className="form-control"
                  value={form.bookingUrl}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      bookingUrl: e.target.value
                    })
                  }
                  placeholder="https://calendly.com/..."
                />
              </div>

              {/* LEAD SOURCE MASTER DATA */}
              <div className="form-group">
                <label>
                  Lead Sources
                </label>

                {leadSources.length === 0 ? (
                  <div className="text-muted">
                    No Lead Sources are configured in the master data.
                  </div>
                ) : (
                  <div>
                    {leadSources.map((source) => {
                      const sourceName = source.name;

                      return (
                        <label
                          key={
                            source.id ||
                            sourceName
                          }
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            marginRight: 15,
                            marginBottom: 8
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={
                              form.leadSources.includes(
                                sourceName
                              )
                            }
                            onChange={() =>
                              toggleLeadSource(
                                sourceName
                              )
                            }
                          />

                          {sourceName}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ACTIVE */}
              <div className="form-group">
                <label>Active</label>

                <select
                  className="form-control"
                  value={
                    form.isActive
                      ? '1'
                      : '0'
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      isActive:
                        e.target.value === '1'
                    })
                  }
                >
                  <option value="1">
                    Active
                  </option>

                  <option value="0">
                    Inactive
                  </option>
                </select>
              </div>

              {/* ACTIONS */}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShow(false);
                    setEditing(null);
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    saving ||
                    leadSources.length === 0
                  }
                >
                  {saving
                    ? 'Saving...'
                    : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BOOKING APP LIST */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Provider</th>
              <th>Lead Sources</th>
              <th>URL</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {apps.map((app) => (
              <tr key={app.id}>
                <td>
                  <strong>
                    {app.name}
                  </strong>
                </td>

                <td>
                  {app.provider === 'calendly'
                    ? 'Calendly'
                    : 'Microsoft Bookings'}
                </td>

                <td>
                  {Array.isArray(app.leadSources) &&
                  app.leadSources.length > 0
                    ? app.leadSources.join(', ')
                    : '—'}
                </td>

                <td>
                  {app.bookingUrl ||
                    'Uses CRM Microsoft configuration'}
                </td>

                <td>
                  {app.isActive
                    ? 'Active'
                    : 'Inactive'}
                </td>

                <td className="actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => edit(app)}
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() =>
                      remove(app.id)
                    }
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}

            {apps.length === 0 && (
              <tr>
                <td
                  colSpan="6"
                  style={{
                    textAlign: 'center'
                  }}
                >
                  No booking apps configured.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

